import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BookOpen, MapPin, Calendar, X, Heart, Share2, Sparkles, ArrowRight,
  Compass, MessageCircle, Mail, Copy, Check, Edit2, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

// Define the delay function
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const INDIAN_STATES_AND_UTS = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const StoriesPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme(); // 👈 Access Theme State
  const [currentUser, setCurrentUser] = useState(null);
  
  const [selectedStory, setSelectedStory] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [allStories, setAllStories] = useState([]);
  const [likedStories, setLikedStories] = useState({});
  const [shareMenuOpen, setShareMenuOpen] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [readLaterStories, setReadLaterStories] = useState([]);
  const [hoveredCardId, setHoveredCardId] = useState(null); 

  // Form & Editing States
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingStoryId, setEditingStoryId] = useState(null);
  const [newStoryForm, setNewStoryForm] = useState({
    title: '',
    author: '',
    stateUT: '',
    place: '',
    type: 'Adventure', 
    content: '',
    imageFile: null,      
    imagePreview: ''      
  });

  // Comments State
  const [newComment, setNewComment] = useState('');

  const curatedStories = useMemo(() => [
    
  ], []);

  const formatImageUrl = (backendPath) => {
    if (!backendPath) return "https://images.unsplash.com/photo-1506461883276-594a12b11cf3?auto=format&fit=crop&w=800&q=80";
    if (backendPath.startsWith('http://localhost') || backendPath.startsWith('http://127.0.0.1')) {
      const url = new URL(backendPath);
      return url.pathname;
    }
    if (backendPath.startsWith('http')) return backendPath;
    const cleanPath = backendPath.replace('backend/', '');
    return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  };

  const parseStoryData = (story) => {
    let normalizedComments = [];
    
    if (story.comments_by && Array.isArray(story.comments_by)) {
      normalizedComments = story.comments_by.map(commentObj => {
        if (!commentObj) return { user_name: 'Unknown', comment: '' };
        if (typeof commentObj === 'object') {
          const uName = Object.keys(commentObj)[0] || 'Unknown';
          return { user_name: uName, comment: commentObj[uName] || '' };
        }
        return { user_name: 'Unknown', comment: '' };
      });
    } else if (story.comments && Array.isArray(story.comments)) {
      normalizedComments = story.comments.map(comment => ({
        user_name: comment.user_name || 'Unknown',
        comment: comment.comment || ''
      }));
    }

    return {
      id: story.id || story._id,
      userId: story.user_id || '',
      title: story.title || 'Untitled Journey',
      author: story.user_id ? String(story.user_id).split('@')[0] : 'Anonymous',
      state: story.location || 'Unknown Location',
      category: story.expedition_type || 'Adventure',
      date: story.date || '',
      likes: story.likes_count || 0,
      img: story.images && Array.isArray(story.images) && story.images.length > 0 ? formatImageUrl(story.images[0]) : formatImageUrl(null),
      excerpt: story.description ? String(story.description).substring(0, 120) + "..." : "No description available.",
      fullText: story.description || "", 
      rawStoryData: story,
      comments: normalizedComments
    };
  };

  const refreshFeed = useCallback(async (waitForDb = false) => {
    if (waitForDb) await delay(3000);
    try {
      const response = await fetch("/api/get_travel_stories");
      if (response.ok) {
        const data = await response.json();
        const stories = data.travel_stories || data.stories || [];
        const dbStories = stories.map(story => {
          try {
            return parseStoryData(story);
          } catch (err) {
            console.warn("Error parsing story:", story, err);
            return null;
          }
        }).filter(s => s !== null);
        
        dbStories.reverse(); 
        
        setAllStories([...dbStories, ...curatedStories]);
      } else {
        console.error("Failed to fetch stories:", response.status);
        setAllStories(curatedStories);
      }
    } catch (error) {
      console.error("Failed to fetch stories from backend:", error);
      setAllStories(curatedStories);
    }
  }, [curatedStories]);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const res = await fetch("/api/profile", {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setCurrentUser(await res.json());
      } catch (err) { console.error("Failed to fetch profile"); }
    };

    const fetchPinnedStories = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const res = await fetch("/api/get_pinned_travel_stories", {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const pinnedIds = data.pinned_travel_stories.map(s => s.id);
          setReadLaterStories(prev => {
            const mockPins = prev.filter(id => id.toString().startsWith("curated_"));
            return [...new Set([...mockPins, ...pinnedIds])];
          });
        }
      } catch (err) { console.error("Failed to fetch pinned stories"); }
    };

    fetchProfile();
    fetchPinnedStories(); 
    refreshFeed(false); 
  }, [refreshFeed, curatedStories]); 

  const handleOpenCreateModal = () => {
    setEditingStoryId(null);
    setNewStoryForm({ 
      title: '', 
      author: currentUser?.name || '', 
      stateUT: '', 
      place: '', 
      type: 'Adventure', 
      content: '', 
      imageFile: null, 
      imagePreview: '' 
    });
    setIsUploadModalOpen(true);
  };

  const handleEditClick = (e, story) => {
    e.stopPropagation();
    setEditingStoryId(story.id);

    const locationParts = story.state ? story.state.split(', ') : [];
    const possibleState = locationParts[locationParts.length - 1];
    const formStateUT = INDIAN_STATES_AND_UTS.includes(possibleState) ? locationParts.pop() : '';
    const formPlace = locationParts.join(', ') || story.state || '';

    setNewStoryForm({
      title: story.title,
      author: story.author,
      stateUT: formStateUT,
      place: formPlace,
      type: story.category,
      content: story.fullText, 
      imageFile: null,
      imagePreview: story.img 
    });
    setIsUploadModalOpen(true);
  };

  const handleDeleteClick = async (e, storyId) => {
    e.stopPropagation();
    const token = localStorage.getItem('access_token');
    if (!token) return alert("Please log in.");
    if (!window.confirm("Are you sure you want to permanently delete this travel diary?")) return;

    try {
      const response = await fetch(`/api/delete_travel_story/${storyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setAllStories(prev => prev.filter(s => s.id !== storyId));
        setReadLaterStories(prev => prev.filter(id => id !== storyId)); 
        showToast('Story deleted successfully!');
      } else if (response.status === 401) {
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem('access_token');
        navigate('/login'); 
      } else {
        const err = await response.json();
        alert(`Failed to delete: ${err.detail}`);
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Server error occurred while deleting.");
    }
  };

  const handleFetchSingleStory = async (story) => {
    if (story.id.toString().startsWith("curated_")) {
      setSelectedStory(story);
      return;
    }

    try {
      const response = await fetch(`/api/get_travel_story/${story.id}`);
      if (response.ok) {
        const data = await response.json();
        const likesArray = Array.isArray(data.likes) ? data.likes : [];
        const commentsArray = Array.isArray(data.comments) ? data.comments : [];

        if (currentUser) {
            const hasLiked = likesArray.some(like => like.user_id === currentUser.email);
            setLikedStories(prev => ({ ...prev, [story.id]: hasLiked }));
        }

        setSelectedStory({
          ...story,
          title: data.title || story.title,
          fullText: data.description || story.fullText,
          likes: likesArray.length,
          comments: commentsArray,
          img: data.images?.length > 0 ? formatImageUrl(data.images[0]) : formatImageUrl(null),
        });
      } else {
        setSelectedStory(story); 
      }
    } catch (error) {
      setSelectedStory(story);
    }
  };

  const handleSaveUploadedStory = async (e) => {
    e.preventDefault();
    if (!newStoryForm.title || !newStoryForm.content || !newStoryForm.stateUT || !newStoryForm.place) {
      alert("Please complete the Title, State/UT, Place, and Content fields before posting!");
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert("You must be logged in to modify a story.");
      return;
    }

    const formData = new FormData();
    formData.append('expedition_type', newStoryForm.type);
    formData.append('title', newStoryForm.title);
    formData.append('description', newStoryForm.content);
    
    const combinedLocation = `${newStoryForm.place}, ${newStoryForm.stateUT}`;
    formData.append('location', combinedLocation);
    
    formData.append('date', new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date()));
    formData.append('tags', 'diary, user-story'); 
    
    if (newStoryForm.imageFile) {
      formData.append('image', newStoryForm.imageFile);
    }

    const url = editingStoryId 
      ? `/api/update_travel_story/${editingStoryId}`
      : "/api/create_travel_story";
    const method = editingStoryId ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        showToast(editingStoryId ? 'Story updated successfully!' : 'Your Travel Story has been published!');
        setIsUploadModalOpen(false);
        refreshFeed(true); 
      } else if (response.status === 401) {
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem('access_token');
        navigate('/login'); 
      } else {
        const err = await response.json();
        alert(`Failed: ${err.detail}`);
      }
    } catch (error) {
      console.error("Upload/Update error:", error);
      alert("Server error occurred.");
    }
  };

  const handleLike = async (e, storyId) => {
    e.stopPropagation();

    if (storyId.toString().startsWith("curated_")) {
      const currentlyLiked = likedStories[storyId];
      setLikedStories(prev => ({ ...prev, [storyId]: !currentlyLiked }));
      setAllStories(prev => prev.map(s => s.id === storyId ? { ...s, likes: currentlyLiked ? s.likes - 1 : s.likes + 1 } : s));
      
      if (selectedStory && selectedStory.id === storyId) {
          setSelectedStory(prev => ({ ...prev, likes: currentlyLiked ? prev.likes - 1 : prev.likes + 1 }));
      }
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert("Please log in to like stories.");
      return;
    }

    try {
      const response = await fetch(`/api/travel_story/${storyId}/toggle_like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const isLiked = data.action === "liked";

        setLikedStories(prev => ({ ...prev, [storyId]: isLiked }));
        setAllStories(prev => prev.map(s => s.id === storyId ? { ...s, likes: isLiked ? s.likes + 1 : s.likes - 1 } : s));

        if (selectedStory && selectedStory.id === storyId) {
           setSelectedStory(prev => ({ ...prev, likes: isLiked ? prev.likes + 1 : prev.likes - 1 }));
        }
      } else if (response.status === 401) {
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem('access_token');
        navigate('/login');
      }
    } catch (error) {
      console.error("Like toggle error:", error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert("Please log in to comment.");
      return;
    }

    try {
      const response = await fetch(`/api/travel_story/${selectedStory.id}/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment: newComment })
      });

      if (response.ok) {
        const data = await response.json();
        const normalizedComment = data.comment 
          ? { user_name: data.comment.user_name || 'Unknown', comment: data.comment.comment || '' }
          : null;
        
        if (normalizedComment) {
          setSelectedStory(prev => ({
            ...prev,
            comments: [...(prev.comments || []), normalizedComment]
          }));
        }
        setNewComment('');
        showToast('Comment added!');
        refreshFeed(true);
      } else {
        console.error("Comment API error:", response.status);
        showToast('Failed to add comment');
      }
    } catch (error) {
      console.error("Comment error:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;

    const token = localStorage.getItem('access_token');
    try {
      const response = await fetch(`/api/travel_story/${selectedStory.id}/comment/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSelectedStory(prev => ({
          ...prev,
          comments: prev.comments.filter(c => c._id !== commentId)
        }));
        showToast('Comment deleted!');
        refreshFeed(true);
      }
    } catch (error) {
      console.error("Delete comment error:", error);
    }
  };

  const handleReadLaterToggle = async (e, storyId) => {
    e.stopPropagation();

    if (storyId.toString().startsWith("curated_")) {
      const isSaved = readLaterStories.includes(storyId);
      setReadLaterStories(prev => 
        isSaved ? prev.filter(id => id !== storyId) : [...prev, storyId]
      );
      showToast(isSaved ? 'Removed from Saved Items' : 'Pinned to Read Later!');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert("Please log in to pin stories.");
      return;
    }

    try {
      const response = await fetch("/api/toggle_pin_story", {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ story_id: storyId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.action === "pinned") {
          setReadLaterStories(prev => [...prev, storyId]);
          showToast('Pinned to Read Later!');
        } else {
          setReadLaterStories(prev => prev.filter(id => id !== storyId));
          showToast('Removed from Saved Items');
        }
      } else if (response.status === 401) {
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem('access_token');
        navigate('/login'); 
      } else {
        showToast("Failed to pin story.");
      }
    } catch (error) {
      console.error("Pin toggle error:", error);
      showToast("Server error occurred.");
    }
  };

  const handleShareClick = async (e, story) => {
    e.stopPropagation();
    const shareData = { title: story.title, text: `Check out this diary: "${story.title}"`, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) { /* ignore */ }
    } else {
      setShareMenuOpen(shareMenuOpen === story.id ? null : story.id);
    }
  };

  const executeFallbackShare = (e, type, story) => {
    e.stopPropagation();
    const shareText = `Check out this travel diary: "${story.title}"`;
    const shareUrl = window.location.href;
    if (type === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank');
    else if (type === 'email') window.location.href = `mailto:?subject=Odessey Travel Diary&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    else if (type === 'copy') { navigator.clipboard.writeText(`${shareText} ${shareUrl}`); showToast('Link copied!'); setShareMenuOpen(null); }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) return alert("Photo must be under 2MB.");
      setNewStoryForm(prev => ({ ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) }));
    }
  };

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const categories = ['All', 'Nature', 'Culture', 'Adventure', 'Relaxation', 'Offbeat'];
  const baseFilteredStories = activeCategory === 'All' ? allStories : allStories.filter(story => story.category === activeCategory);
  const pinnedStories = baseFilteredStories.filter(story => readLaterStories.includes(story.id));
  const regularStories = baseFilteredStories.filter(story => !readLaterStories.includes(story.id));

  const renderStoryCard = (story, isPinned) => {
    const isLiked = likedStories[story.id];
    const displayLikes = story.likes; 
    const isSavedLater = readLaterStories.includes(story.id);
    const isOwner = currentUser && story.userId === currentUser.email;
    const isHovered = hoveredCardId === story.id;

    return (
      <div
        key={story.id}
        onClick={() => handleFetchSingleStory(story)}
        style={{
          backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', overflow: 'hidden', 
          border: isPinned ? '2px solid #16a34a' : (isDark ? '1px solid #374151' : '1px solid #e5e7eb'),
          boxShadow: isHovered 
            ? (isPinned ? '0 30px 60px rgba(22, 163, 74, 0.25)' : (isDark ? '0 30px 60px rgba(0,0,0,0.7)' : '0 30px 60px rgba(0,0,0,0.08)'))
            : (isPinned ? '0 15px 35px rgba(22, 163, 74, 0.15)' : (isDark ? '0 10px 25px rgba(0,0,0,0.4)' : '0 10px 25px rgba(0,0,0,0.03)')),
          transform: isHovered ? 'translateY(-12px)' : 'translateY(0)',
          transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease, background-color 0.3s, border-color 0.3s',
          cursor: 'pointer', display: 'flex', flexDirection: 'column', position: 'relative', boxSizing: 'border-box'
        }}
        onMouseEnter={() => setHoveredCardId(story.id)}
        onMouseLeave={() => {
          setHoveredCardId(null);
          setShareMenuOpen(null);
        }}
      >
        <div style={{ height: '220px', overflow: 'hidden', position: 'relative', backgroundColor: isDark ? '#374151' : '#e5e7eb' }}>
          <img src={story.img} alt={story.title} className="card-photo-element" style={{ width: '100%', height: '100%', objectFit: 'cover', transform: isHovered ? 'scale(1.06)' : 'scale(1)', filter: isHovered ? 'brightness(0.95) contrast(1.04)' : 'none', transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), filter 0.6s ease' }} />
         
          <button onClick={(e) => handleReadLaterToggle(e, story.id)} style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, backgroundColor: isSavedLater ? '#16a34a' : (isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)'), color: isSavedLater ? 'white' : (isDark ? '#f9fafb' : '#111827'), border: 'none', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', cursor: 'pointer', transition: 'all 0.2s ease', fontSize: '15px' }} title="Save for Read Later">
            {isSavedLater ? '✓' : '🔖'}
          </button>
          
          {isOwner && (
            <div style={{ position: 'absolute', top: '62px', right: '16px', display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 10 }}>
              <button onClick={(e) => handleEditClick(e, story)} style={{ backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)', color: '#0284c7', border: 'none', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', cursor: 'pointer' }} title="Edit Story">
                <Edit2 size={15} />
              </button>
              <button onClick={(e) => handleDeleteClick(e, story.id)} style={{ backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)', color: '#ef4444', border: 'none', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', cursor: 'pointer' }} title="Delete Story">
                <Trash2 size={15} />
              </button>
            </div>
          )}

          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, transparent 40%, rgba(0,0,0,0.35) 100%)', pointerEvents: 'none' }} />

          <div style={{ position: 'absolute', top: '16px', left: '16px', display: 'flex', gap: '6px', zIndex: 2, flexWrap: 'wrap', maxWidth: '70%' }}>
            <span style={{ backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.95)', backdropFilter: 'blur(4px)', color: isDark ? '#f9fafb' : '#1f2937', padding: '5px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
              <MapPin size={11} color="#16a34a" /> {story.state}
            </span>
            <span style={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(4px)', color: 'white', padding: '5px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold' }}>
              {story.category}
            </span>
          </div>
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1, boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '600', color: isOwner ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#4ade80' : '#16a34a') }}>{isOwner ? 'My Diary' : `By ${story.author}`}</span>
            <span>•</span>
            <span>{story.date}</span>
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '10px', lineHeight: '1.3' }}>{story.title}</h3>
          <p style={{ fontSize: '14px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.6', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '16px' }}>{story.excerpt}</p>
         
          {/* Top 3 Comments Section Rendered Directly on Card */}
          {story.comments && story.comments.length > 0 && (
            <div style={{ marginTop: 'auto', marginBottom: '16px', padding: '12px', backgroundColor: isDark ? '#111827' : '#f9fafb', borderRadius: '14px', border: isDark ? '1px solid #374151' : '1px solid #f3f4f6', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <MessageCircle size={13} color="#6b7280" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Latest Comments</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {story.comments.slice(0, 3).map((c, idx) => (
                  <div key={idx} style={{ fontSize: '12px', color: isDark ? '#d1d5db' : '#4b5563', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>
                    <span style={{ fontWeight: '600', color: isDark ? '#f9fafb' : '#1f2937', marginRight: '6px' }}>{c.user_name}</span>
                    {c.comment}
                  </div>
                ))}
              </div>
              {story.comments.length > 3 && (
                <div style={{ fontSize: '11px', color: isDark ? '#4ade80' : '#16a34a', marginTop: '8px', fontWeight: '600' }}>
                  View all {story.comments.length} comments
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: isDark ? '1px solid #374151' : '1px solid #f3f4f6', paddingTop: '16px', marginTop: story.comments && story.comments.length > 0 ? '0' : 'auto', position: 'relative' }}>
            <button style={{ background: 'none', border: 'none', color: isDark ? '#4ade80' : '#16a34a', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', padding: 0, cursor: 'pointer' }}>
              Read Diary <ArrowRight size={15} />
            </button>
           
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={(e) => handleLike(e, story.id)} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: isLiked ? '#ef4444' : (isDark ? '#9ca3af' : '#9ca3af'), fontSize: '13px', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s' }}>
                <Heart size={17} fill={isLiked ? '#ef4444' : 'none'} /> {displayLikes}
              </button>
             
              <div style={{ position: 'relative' }}>
                <button onClick={(e) => handleShareClick(e, story)} style={{ display: 'flex', alignItems: 'center', color: shareMenuOpen === story.id ? '#0284c7' : (isDark ? '#9ca3af' : '#9ca3af'), background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.25s' }}>
                  <Share2 size={17} />
                </button>
                {shareMenuOpen === story.id && (
                  <div style={{ position: 'absolute', bottom: '100%', right: '0', marginBottom: '8px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', overflow: 'hidden', zIndex: 50, width: '150px', animation: 'slideUp 0.2s ease-out' }}>
                    <button onClick={(e) => executeFallbackShare(e, 'whatsapp', story)} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: 'none', borderBottom: isDark ? '1px solid #374151' : '1px solid #f3f4f6', background: isDark ? '#1f2937' : 'white', cursor: 'pointer', color: isDark ? '#f9fafb' : '#374151', fontSize: '13px', fontWeight: '500' }}>
                      <MessageCircle size={15} color="#25D366" /> WhatsApp
                    </button>
                    <button onClick={(e) => executeFallbackShare(e, 'email', story)} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: 'none', borderBottom: isDark ? '1px solid #374151' : '1px solid #f3f4f6', background: isDark ? '#1f2937' : 'white', cursor: 'pointer', color: isDark ? '#f9fafb' : '#374151', fontSize: '13px', fontWeight: '500' }}>
                      <Mail size={15} color="#0284c7" /> Email
                    </button>
                    <button onClick={(e) => executeFallbackShare(e, 'copy', story)} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: 'none', background: isDark ? '#1f2937' : 'white', cursor: 'pointer', color: isDark ? '#f9fafb' : '#374151', fontSize: '13px', fontWeight: '500' }}>
                      <Copy size={15} color="#6b7280" /> Copy Link
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', overflowX: 'hidden', transition: 'background-color 0.3s' }}>
     
      <div style={{ 
        position: 'relative', 
        height: '45vh', 
        minHeight: '380px', 
        backgroundImage: 'url(https://www.himalayanecotourism.com/wp-content/uploads/2023/06/ladakh-matho-gompa-01.jpg)', 
        backgroundSize: 'cover', 
        backgroundPosition: 'center',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        paddingTop: '60px'
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(17, 24, 39, 0.6), rgba(17, 24, 39, 0.95))' }} />
        
        <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', width: '100%', padding: '0 16px', boxSizing: 'border-box' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', padding: '6px 14px', borderRadius: '50px', color: 'white', fontSize: '13px', fontWeight: '600', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.3)' }}>
            <Sparkles size={14} /> Real Experiences, Real Explorers
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: '800', color: 'white', marginBottom: '12px', textShadow: '0 4px 12px rgba(0,0,0,0.4)', letterSpacing: '-1px' }}>
            Travel Diaries
          </h1>
          <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'rgba(255,255,255,0.85)', maxWidth: '600px', margin: '0 auto', fontWeight: '400', lineHeight: '1.5' }}>
            Discover India through the eyes of our travellers. Get inspired, find hidden gems, and start planning your next chapter.
          </p>
         
          <button
            onClick={handleOpenCreateModal}
            style={{ marginTop: '20px', padding: '12px 24px', borderRadius: '50px', backgroundColor: '#16a34a', color: 'white', fontSize: '14px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 10px 20px rgba(22, 163, 74, 0.3)', transition: 'all 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
          >
            + Add Your Travel Photo & Story
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', boxSizing: 'border-box' }}>
       
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '-25px', position: 'relative', zIndex: 10, marginBottom: '40px' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '10px 20px', borderRadius: '50px', fontSize: '13px',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease',
                backgroundColor: activeCategory === cat ? '#16a34a' : (isDark ? '#1f2937' : 'white'),
                color: activeCategory === cat ? 'white' : (isDark ? '#d1d5db' : '#4b5563'),
                border: activeCategory === cat ? '1px solid #16a34a' : (isDark ? '1px solid #374151' : '1px solid #e5e7eb'),
                boxShadow: activeCategory === cat ? '0 10px 20px rgba(22, 163, 74, 0.25)' : (isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.05)')
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: '60px' }}>
          {pinnedStories.length > 0 && (
            <div style={{ marginBottom: '40px', padding: '25px 20px', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', borderRadius: '24px', border: isDark ? '2px dashed #065f46' : '2px dashed #4ade80', boxShadow: 'inset 0 2px 8px rgba(22, 163, 74, 0.04)', boxSizing: 'border-box' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '800', color: isDark ? '#a7f3d0' : '#166534', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📌 Pinned for Later Collection ({pinnedStories.length})
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' }}>
                {pinnedStories.map(story => renderStoryCard(story, true))}
              </div>
            </div>
          )}

          <h3 style={{ fontSize: '20px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '20px' }}>
            {activeCategory === 'All' ? 'All Diaries Feed' : `${activeCategory} Feed`}
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' }}>
            {regularStories.length > 0 ? (
              regularStories.map(story => renderStoryCard(story, false))
            ) : (
              <p style={{ color: isDark ? '#9ca3af' : '#6b7280', gridColumn: '1 / -1', textAlign: 'center', padding: '40px 16px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', fontSize: '14px' }}>
                All active diaries in this section have been saved to your Read Later container above!
              </p>
            )}
          </div>
        </div>

        <div style={{ backgroundColor: isDark ? '#030712' : '#111827', borderRadius: '24px', padding: '40px 20px', textAlign: 'center', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 20px 40px rgba(17, 24, 39, 0.2)', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }}>
          <div style={{ position: 'absolute', top: '-30px', left: '-30px', opacity: 0.1 }}><Compass size={250} /></div>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: '800', marginBottom: '12px' }}>Ready to write your own story?</h2>
            <p style={{ fontSize: '15px', color: '#9ca3af', maxWidth: '600px', margin: '0 auto 25px', lineHeight: '1.6' }}>Stop dreaming and start packing. Let our travel experts craft the perfect customized itinerary for your next adventure.</p>
            <button onClick={() => navigate('/plan')} style={{ padding: '14px 30px', borderRadius: '50px', backgroundColor: '#16a34a', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'all 0.3s', boxShadow: '0 8px 20px rgba(22, 163, 74, 0.3)', display: 'inline-flex', alignItems: 'center', gap: '8px' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Plan My Trip <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {isUploadModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
          <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', width: '100%', maxWidth: '550px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '20px', padding: '30px 20px', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', animation: 'slideUp 0.3s ease-out forwards', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }} className="hide-scrollbar">
            <button onClick={() => setIsUploadModalOpen(false)} style={{ position: 'absolute', top: '15px', right: '15px', backgroundColor: isDark ? '#374151' : '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#111827' }}>
              <X size={16} />
            </button>
            <h3 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '6px', color: isDark ? '#f9fafb' : '#111827' }}>{editingStoryId ? "Edit Your Experience" : "Share Your Experience"}</h3>
            <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '13px', marginBottom: '20px' }}>{editingStoryId ? "Update your travel details and memories below." : "Upload a photo and tell everyone about your incredible journey."}</p>
           
            <form onSubmit={handleSaveUploadedStory}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>Travel Diary Title</label>
                <input type="text" placeholder="e.g., Solitude among the Pines" value={newStoryForm.title} onChange={e => setNewStoryForm(prev => ({ ...prev, title: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 100%' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>Your Name</label>
                  <input type="text" placeholder="e.g., Rahul K." value={newStoryForm.author} onChange={e => setNewStoryForm(prev => ({ ...prev, author: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} disabled={!!editingStoryId} />
                </div>
                
                <div style={{ flex: '1 1 45%' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>State / UT</label>
                  <select 
                    value={newStoryForm.stateUT} 
                    onChange={e => setNewStoryForm(prev => ({ ...prev, stateUT: e.target.value }))} 
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827', boxSizing: 'border-box' }}
                  >
                    <option value="" disabled>Select State/UT</option>
                    {INDIAN_STATES_AND_UTS.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                
                <div style={{ flex: '1 1 45%' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>Place</label>
                  <input 
                    type="text" 
                    placeholder="e.g., Manali" 
                    value={newStoryForm.place} 
                    onChange={e => setNewStoryForm(prev => ({ ...prev, place: e.target.value }))} 
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} 
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>Vibe Checklist Category</label>
                <select value={newStoryForm.type} onChange={e => setNewStoryForm(prev => ({ ...prev, type: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827', boxSizing: 'border-box' }}>
                  <option value="Adventure">Adventure</option>
                  <option value="Relaxation">Relaxation</option>
                  <option value="Culture">Culture</option>
                  <option value="Nature">Nature</option>
                  <option value="Offbeat">Offbeat</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>Upload Cover Image</label>
                <div style={{ position: 'relative', border: isDark ? '2px dashed #065f46' : '2px dashed #16a34a', borderRadius: '14px', padding: '16px', textAlign: 'center', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', cursor: 'pointer', overflow: 'hidden' }}>
                  {newStoryForm.imagePreview ? (
                    <div>
                      <img src={newStoryForm.imagePreview} alt="Preview" style={{ width: '100%', maxHeight: '130px', objectFit: 'cover', borderRadius: '10px' }} />
                      <button type="button" onClick={(e) => { e.preventDefault(); setNewStoryForm(prev => ({ ...prev, imageFile: null, imagePreview: '' })); }} style={{ marginTop: '8px', color: '#ef4444', fontSize: '12px', fontWeight: '600', border: 'none', background: 'none', cursor: 'pointer' }}>Remove/Change Image</button>
                    </div>
                  ) : (
                    <div>
                      <p style={{ fontSize: '13px', margin: '0 0 3px', fontWeight: '500', color: isDark ? '#a7f3d0' : '#166534' }}>Click to select a travel memory photo</p>
                      <p style={{ fontSize: '11px', color: isDark ? '#6ee7b7' : '#16a34a', margin: 0 }}>Max size: 2MB (Supports PNG, JPG)</p>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleImageFileChange} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '5px', color: isDark ? '#d1d5db' : '#374151' }}>Your Travel Story</label>
                <textarea rows={4} placeholder="Write about local cuisine, pathways, culture, or feelings experienced along the journey..." value={newStoryForm.content} onChange={e => setNewStoryForm(prev => ({ ...prev, content: e.target.value }))} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '14px', lineHeight: '1.6', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
              </div>
             
              <button type="submit" style={{ width: '100%', padding: '12px', borderRadius: '50px', backgroundColor: '#16a34a', color: 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '15px', boxShadow: '0 5px 15px rgba(22, 163, 74, 0.2)' }}>
                {editingStoryId ? "Save Changes" : "Publish Diary Entry"}
              </button>
            </form>
          </div>
        </div>
      )}

      {selectedStory && (
        <div onClick={() => { setSelectedStory(null); setShareMenuOpen(null); }} style={{ position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
          <div onClick={(e) => { e.stopPropagation(); setShareMenuOpen(null); }} style={{ backgroundColor: isDark ? '#1f2937' : 'white', width: '100%', maxWidth: '850px', maxHeight: '90vh', borderRadius: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', animation: 'slideUp 0.3s ease-out forwards', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }}>
            <button onClick={() => setSelectedStory(null)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', zIndex: 10, transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}>
              <X size={20} />
            </button>
           
            <div style={{ height: '280px', flexShrink: 0, position: 'relative' }}>
              <img src={selectedStory.img} alt={selectedStory.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(17, 24, 39, 0.9), transparent)' }} />
              <div style={{ position: 'absolute', bottom: '20px', left: '20px', right: '20px' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <span style={{ backgroundColor: '#16a34a', color: 'white', padding: '5px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <BookOpen size={13} /> Travel Diary
                  </span>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', color: 'white', padding: '5px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <MapPin size={13} /> {selectedStory.state}
                  </span>
                </div>
                <h2 style={{ color: 'white', fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: '800', lineHeight: '1.2' }}>{selectedStory.title}</h2>
              </div>
            </div>
           
            <div style={{ padding: '25px 20px', overflowY: 'auto', backgroundColor: isDark ? '#111827' : '#f9fafb', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px', borderBottom: isDark ? '1px solid #374151' : '1px solid #e5e7eb', paddingBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '46px', height: '46px', borderRadius: '50%', backgroundColor: isDark ? '#082f49' : '#e0f2fe', color: isDark ? '#38bdf8' : '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' }}>
                    {selectedStory.author.charAt(0)}
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1f2937', fontSize: '16px' }}>{selectedStory.author}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '13px', marginTop: '3px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={13} /> {selectedStory.date}</span>
                    </div>
                  </div>
                </div>
               
                <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
                  <button onClick={(e) => handleLike(e, selectedStory.id)} style={{ width: '40px', height: '38px', borderRadius: '50%', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', backgroundColor: isDark ? '#1f2937' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: likedStories[selectedStory.id] ? '#ef4444' : (isDark ? '#9ca3af' : '#6b7280'), cursor: 'pointer', transition: 'all 0.2s' }}>
                    <Heart size={16} fill={likedStories[selectedStory.id] ? '#ef4444' : 'none'} />
                  </button>
                  <button onClick={(e) => handleShareClick(e, selectedStory)} style={{ width: '38px', height: '38px', borderRadius: '50%', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', backgroundColor: isDark ? '#1f2937' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: shareMenuOpen === selectedStory.id ? '#0284c7' : (isDark ? '#9ca3af' : '#6b7280'), cursor: 'pointer', transition: 'all 0.2s' }}>
                    <Share2 size={16} />
                  </button>
                 
                  {shareMenuOpen === selectedStory.id && (
                    <div style={{ position: 'absolute', top: '100%', right: '0', marginTop: '8px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', overflow: 'hidden', zIndex: 50, width: '150px', animation: 'slideUp 0.2s ease-out' }}>
                      <button onClick={(e) => executeFallbackShare(e, 'whatsapp', selectedStory)} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: 'none', borderBottom: isDark ? '1px solid #374151' : '1px solid #f3f4f6', background: isDark ? '#1f2937' : 'white', cursor: 'pointer', color: isDark ? '#f9fafb' : '#374151', fontSize: '13px', fontWeight: '500' }}>
                        <MessageCircle size={15} color="#25D366" /> WhatsApp
                      </button>
                      <button onClick={(e) => executeFallbackShare(e, 'email', selectedStory)} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: 'none', borderBottom: isDark ? '1px solid #374151' : '1px solid #f3f4f6', background: isDark ? '#1f2937' : 'white', cursor: 'pointer', color: isDark ? '#f9fafb' : '#374151', fontSize: '13px', fontWeight: '500' }}>
                        <Mail size={15} color="#0284c7" /> Email
                      </button>
                      <button onClick={(e) => executeFallbackShare(e, 'copy', selectedStory)} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: 'none', background: isDark ? '#1f2937' : 'white', cursor: 'pointer', color: isDark ? '#f9fafb' : '#374151', fontSize: '13px', fontWeight: '500' }}>
                        <Copy size={15} color="#6b7280" /> Copy Link
                      </button>
                    </div>
                  )}
                </div>
              </div>
             
              <div style={{ fontSize: '16px', lineHeight: '1.8', color: isDark ? '#d1d5db' : '#374151', whiteSpace: 'pre-line' }}>
                <p style={{ fontSize: '18px', fontStyle: 'italic', color: isDark ? '#f9fafb' : '#111827', marginBottom: '25px', borderLeft: `3px solid ${isDark ? '#4ade80' : '#16a34a'}`, paddingLeft: '16px' }}>
                  "{selectedStory.excerpt}"
                </p>
                {selectedStory.fullText}
              </div>

              {/* COMMENTS SECTION */}
              <div style={{ marginTop: '30px', paddingTop: '25px', borderTop: isDark ? '1px solid #374151' : '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: isDark ? '#f9fafb' : '#111827' }}>
                  Comments ({selectedStory.comments?.length || 0})
                </h3>

                <form onClick={(e) => e.stopPropagation()} onSubmit={handleAddComment} style={{ display: 'flex', gap: '10px', marginBottom: '25px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{ flex: 1, minWidth: '200px', padding: '10px 14px', borderRadius: '50px', border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827', boxSizing: 'border-box' }}
                  />
                  <button type="submit" style={{ padding: '10px 20px', borderRadius: '50px', backgroundColor: '#16a34a', color: 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s', fontSize: '13px' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}>
                    Post
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {selectedStory.comments && selectedStory.comments.map((comment, index) => (
                    <div key={comment._id || index} style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: isDark ? '#082f49' : '#e0f2fe', color: isDark ? '#38bdf8' : '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: '0', fontSize: '14px' }}>
                        {comment.user_name ? comment.user_name.charAt(0) : 'U'}
                      </div>
                      <div style={{ flex: 1, backgroundColor: isDark ? '#1f2937' : 'white', padding: '14px', borderRadius: '14px', border: isDark ? '1px solid #374151' : '1px solid #f3f4f6', boxSizing: 'border-box' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '13px', color: isDark ? '#f9fafb' : '#111827' }}>{comment.user_name || 'User'}</span>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                              {comment.created_at ? new Date(comment.created_at).toLocaleDateString() : 'Recently'}
                            </span>
                            {currentUser && currentUser.email === comment.user_id && (
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment._id); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0' }} title="Delete Comment">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                        <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#4b5563', margin: 0, lineHeight: '1.5' }}>{comment.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
             
              <div style={{ marginTop: '35px', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', padding: '24px 20px', borderRadius: '16px', textAlign: 'center', border: isDark ? '1px solid #065f46' : '1px solid #bbf7d0', boxSizing: 'border-box' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#a7f3d0' : '#15803d', marginBottom: '8px' }}>Inspired by this story?</h4>
                <p style={{ fontSize: '14px', color: isDark ? '#d1fae5' : '#166534', marginBottom: '16px' }}>Explore top cities, spots, and hidden gems in {selectedStory.state}.</p>
                <button onClick={() => navigate(`/state/${encodeURIComponent(selectedStory.state)}`)} style={{ padding: '12px 24px', borderRadius: '50px', backgroundColor: '#16a34a', color: 'white', fontSize: '14px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}>
                  Explore {selectedStory.state}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div style={{ position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#111827', color: 'white', padding: '10px 20px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', zIndex: 9999, animation: 'slideUp 0.3s ease-out forwards' }}>
          <Check size={15} color="#4ade80" /> {toastMessage}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default StoriesPage;